import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DUES_FUND, MONEY_LIMITS, RECEIPT_METHODS, type ReceiptMethod, type ReceiptRow } from '@mahallu/shared';
import { ApiError, MONEY_KEYS, money, useAppSettings, useFunds, useHouseAccount } from '../api';
import { FormFooter, FormSection, Modal, NumberField, SelectField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { inr, monthLabel, todayIst } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

/**
 * Collect money and issue a numbered receipt (pilot slice 7). With a house it defaults to varisankhya
 * for what is outstanding; without one it records a donation from a named person.
 */
export function CollectForm({ house, onClose }: { house: { id: string; houseNo: string; nameEn: string } | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const funds = useFunds();
  const account = useHouseAccount(house?.id ?? null);
  const [fundCode, setFundCode] = useState(house ? DUES_FUND : '');
  const [amount, setAmount] = useState<number | ''>('');
  const [ahead, setAhead] = useState<number | ''>(0);
  const [method, setMethod] = useState<ReceiptMethod | ''>('CASH');
  const [reference, setReference] = useState('');
  const [receivedOn, setReceivedOn] = useState(todayIst());
  const [payer, setPayer] = useState('');
  const [note, setNote] = useState('');
  const appSettings = useAppSettings();
  const [notify, setNotify] = useState(true);
  // The mahallu decides whether "send to the family" starts ticked (Settings › App settings).
  useEffect(() => {
    if (appSettings.data) setNotify(appSettings.data.receiptNotifyDefault);
  }, [appSettings.data?.receiptNotifyDefault]);
  const [issued, setIssued] = useState<ReceiptRow | null>(null);
  const dues = fundCode === DUES_FUND;
  const a = account.data;
  const months = Number(ahead || 0);
  const suggested = a ? a.outstandingInr + months * a.monthlyRateInr : 0;

  const save = useMutation({
    mutationFn: () =>
      money.issueReceipt({
        fundCode,
        houseId: house?.id ?? null,
        payerName: house ? null : payer.trim(),
        amountInr: Number(amount === '' && dues ? suggested : amount),
        method: method as ReceiptMethod,
        reference: reference.trim() || null,
        receivedOn,
        note: note.trim() || null,
        monthsAhead: dues ? months : undefined,
        notify,
      }),
    onSuccess: async (r) => {
      setIssued(r);
      await Promise.all(MONEY_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });

  if (issued) {
    return (
      <Modal
        title={t('collect.issuedTitle')}
        onClose={onClose}
        footer={
          <>
            <span className="spacer" />
            <Link className="btn" to={`/receipts/${issued.id}/print`} target="_blank" rel="noopener">
              <Icon name="file" />
              {t('collect.print')}
            </Link>
            <button type="button" className="btn primary" onClick={onClose}>
              {t('users.done')}
            </button>
          </>
        }
      >
        <div className="callout" role="status">
          {t('collect.issued', { no: issued.receiptNo, amount: inr(issued.amountInr) })}
          {issued.periods.length ? <div className="note">{t('collect.forMonths', { months: issued.periods.map((p) => monthLabel(p, i18n.language)).join(', ') })}</div> : null}
        </div>
        <p className="note" role="status" style={{ marginTop: 10 }}>
          {issued.notice ? t('notice.queued', { to: issued.notice.toMasked }) : t(`notice.skip.${issued.noticeSkipped ?? 'NOT_REQUESTED'}`)}
        </p>
      </Modal>
    );
  }

  const active = (funds.data ?? []).filter((f) => f.active && (house || f.kind !== 'DUES'));
  return (
    <Modal
      title={house ? `${t('collect.title')} · ${house.houseNo} ${house.nameEn}` : t('collect.donationTitle')}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('collect.issue')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {house && a ? (
          <p className="callout small">
            {a.outstandingInr ? t('collect.outstanding', { amount: inr(a.outstandingInr), months: a.dues.filter((d) => d.paidInr < d.amountInr).length }) : t('collect.nothingDue')}
            {a.monthlyRateInr ? ` · ${t('collect.monthly', { amount: inr(a.monthlyRateInr) })}` : ''}
          </p>
        ) : null}
        <FormSection title={t('collect.section')}>
          <SelectField
            label={t('collect.fund')}
            value={fundCode}
            onChange={setFundCode}
            required
            empty={t('form.choose')}
            options={active.map((f) => ({ value: f.code, label: i18n.language === 'ml' && f.nameMl ? f.nameMl : f.nameEn }))}
          />
          {dues ? (
            <NumberField label={t('collect.ahead')} value={ahead} onChange={setAhead} min={0} max={MONEY_LIMITS.monthsAhead} hint={t('collect.aheadHint')} />
          ) : !house ? (
            <TextField label={t('collect.payer')} value={payer} onChange={setPayer} maxLength={MONEY_LIMITS.payer} required />
          ) : null}
          <NumberField
            label={t('collect.amount')}
            value={amount === '' && dues ? suggested || '' : amount}
            onChange={setAmount}
            min={1}
            max={MONEY_LIMITS.amount}
            required
            hint={dues ? t('collect.amountHint') : undefined}
          />
          <SelectField<ReceiptMethod> label={t('collect.method')} value={method} onChange={setMethod} required options={RECEIPT_METHODS.map((m) => ({ value: m, label: t(`method.${m}`) }))} />
          {method && method !== 'CASH' ? <TextField label={t('collect.reference')} value={reference} onChange={setReference} maxLength={MONEY_LIMITS.reference} /> : null}
          <TextField label={t('collect.receivedOn')} type="date" value={receivedOn} onChange={setReceivedOn} max={todayIst()} required />
          <TextField label={t('collect.note')} value={note} onChange={setNote} maxLength={MONEY_LIMITS.note} full />
          {house ? (
            <div className="fld full">
              <label className="check">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                {t('collect.notify')}
              </label>
            </div>
          ) : null}
        </FormSection>
        <p className="note">{t('drawer.arrearsNote')}</p>
      </form>
    </Modal>
  );
}
